import React, { useContext } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PropTypes from "prop-types";
import styled, { ThemeProvider, ThemeContext } from "styled-components";
import { css } from "@styled-system/css";
import { space, color, typography, compose, variant } from "styled-system";
import { themeGet } from "@styled-system/theme-get";

/**
 * Using 3rd party icons from Font Awesome.
 *
 * For full list of icons see here: <https://fontawesome.com/icons?d=listing>.
 *
 * Some examples of icons with props for size, colour or animation below. For full usage guidelines view the readme at <https://github.com/FortAwesome/react-fontawesome#usage>
 *
 * Note: There are two different icon set weightings available in the Font Awesome free icons sets. Regular and solid. To use we need to specify which set to use by prefixing icon with either "far" or "fas" as shown in example code below.
 *
 * To get started you can import the icons into Font Awesome library. Alternatively if you want to use their full icon set you need to purchase a PRO plan from Font Awesome, and can install the full pro icon packages in your app. See the readme here for more details on how to use and install PRO: <https://github.com/orchestrated-io/orcs-design-system>
 *
 * Shorthand: pass a **kebab-case icon name string** (e.g. `icon="user"`) for the default UI weight **Pro Regular**, same as `icon={["far", "user"]}`. Use `["fas", "thumbtack"]` or imported definitions when you need another style.
 */

/** FA normalizeIconArgs throws on empty name; keep lookups safe. */
function defaultFarIconName(icon) {
  return typeof icon === "string" && icon.length > 0 ? icon : "question-circle";
}

/** Resolve `icon` for FontAwesomeIcon: string → `["far", name]`, else unchanged. */
function resolveFontAwesomeIconProp(icon) {
  if (typeof icon === "string") {
    return ["far", defaultFarIconName(icon)];
  }
  return icon;
}

const IconStyles = compose(space, color, typography);

const IconWrapper = styled("span")(
  css({ lineHeight: "0", display: "inline-block" }),
  (props) =>
    variant({
      prop: "variant",
      variants: {
        primary: {
          color: themeGet("colors.primary")(props),
        },
        secondary: {
          color: themeGet("colors.secondary")(props),
        },
        success: {
          color: themeGet("colors.successLight")(props),
        },
        warning: {
          color: themeGet("colors.warning")(props),
        },
        danger: {
          color: themeGet("colors.danger")(props),
        },
        grey: {
          color: themeGet("colors.grey")(props),
        },
        greyDark: {
          color: themeGet("colors.greyDark")(props),
        },
        greyDarkest: {
          color: themeGet("colors.greyDarkest")(props),
        },
      },
    }),
  IconStyles
);

const Icon = React.forwardRef(function Icon(
  {
    border,
    mask,
    fixedWidth,
    inverse,
    flip,
    icon,
    listitem,
    pull,
    pulse,
    rotation,
    size,
    spin,
    symbol,
    transform,
    color,
    variant: variantProp,
    theme,
    title,
    ...props
  },
  ref
) {
  const themeFromContext = useContext(ThemeContext);
  const palette =
    (theme && theme.colors) ||
    (themeFromContext && themeFromContext.colors) ||
    undefined;
  const resolvedColor =
    palette && typeof color === "string" && palette[color]
      ? palette[color]
      : color;

  const faIcon = resolveFontAwesomeIconProp(icon);

  const component = (
    <IconWrapper
      color={variantProp ? undefined : resolvedColor}
      variant={variantProp}
      {...props}
      ref={ref}
    >
      <FontAwesomeIcon
        border={border}
        mask={mask}
        fixedWidth={fixedWidth}
        inverse={inverse}
        flip={flip}
        listitem={listitem}
        pull={pull}
        rotation={rotation}
        icon={faIcon}
        pulse={pulse}
        size={size}
        spin={spin}
        symbol={symbol}
        transform={transform}
        title={title && `Icon-${title}`}
        color={resolvedColor}
      />
    </IconWrapper>
  );

  return theme ? (
    <ThemeProvider theme={theme}>{component}</ThemeProvider>
  ) : (
    component
  );
});

Icon.propTypes = {
  /** Colours the icon using colours from the system colour palette */
  color: PropTypes.string,
  /** Variant prop for common color variants (primary, secondary, success, warning, danger, grey, greyDark, greyDarkest) */
  variant: PropTypes.oneOf([
    "primary",
    "secondary",
    "success",
    "warning",
    "danger",
    "grey",
    "greyDark",
    "greyDarkest",
  ]),
  /** Set a border around the icon */
  border: PropTypes.bool,
  /** Set an icon as a clipping mask around another icon */
  mask: PropTypes.string,
  /** Set icon to display as fixed width */
  fixedWidth: PropTypes.bool,
  /** Invert the icon for use on dark backgrounds */
  inverse: PropTypes.bool,
  /** Flip horizontally, vertically, or both */
  flip: PropTypes.oneOf(["horizontal", "vertical", "both"]),
  /** Font Awesome: kebab-case name for default Pro Regular (`"user"` → far + user), solid two-string form, or an imported icon definition object */
  icon: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array,
    PropTypes.object,
  ]),
  /** Set icon to render in the style of a list bullet */
  listitem: PropTypes.bool,
  /** Pull an icon to float to the left or right of the element's other contents */
  pull: PropTypes.oneOf(["left", "right"]),
  /** Set a slow pulse animation on the icon */
  pulse: PropTypes.bool,
  /** Rotate the icon by the specified degree */
  rotation: PropTypes.oneOf(["90", "180", "270"]),
  /** Set the icon size based on FontAwesome usage for size */
  size: PropTypes.string,
  /** Set an 8-frame spinning animation on the icon */
  spin: PropTypes.bool,
  /** Set an icon as a symbol to use with the SVG Sprites technique */
  symbol: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  /** Power transforms to scale and position the icon */
  transform: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  /** Specifies the system design theme. */
  theme: PropTypes.object,
  /** Set an accessabilty title for screen readers. */
  title: PropTypes.string,
};

export default Icon;
